import type { ImageSelectorOption } from "../components/MiniModal/ImageOptionSelector";
import type { TransitModeFilter as ApiTransitModeFilter } from "../types/dtos/api/itinerary/itinerary-search-request.dto";

/** MOTIS transit mode token always sent to the itinerary search API. */
export const ALWAYS_INCLUDED_TRANSIT_MODE_FILTER = "OTHER" as const;

/** Stable UI category ids for transit mode preferences. */
export type TransitModeCategoryId =
  | "BUS" |
  "TRAM" |
  "METRO" |
  "TRAIN" |
  "BOAT" |
  "FUNICULAR";

/** All supported transit mode category ids in UI display order. */
export const TRANSIT_MODE_CATEGORY_IDS: readonly TransitModeCategoryId[] = [
    "BUS",
    "TRAM",
    "METRO",
    "TRAIN",
    "BOAT",
    "FUNICULAR",
] as const;

/** Default enabled transit mode categories (all UI options active). */
export const DEFAULT_ENABLED_TRANSIT_MODE_CATEGORIES: readonly TransitModeCategoryId[] = TRANSIT_MODE_CATEGORY_IDS;

/** Maps each UI category to the MOTIS tokens sent when the category is enabled. */
export const TRANSIT_MODE_CATEGORY_TO_MOTIS_IDS: Record<TransitModeCategoryId, readonly string[]> = {
    "BUS":       [ "BUS", "ODM", "FLEX", "COACH" ],
    "TRAM":      [ "TRAM" ],
    "METRO":     [ "SUBWAY", "METRO" ],
    "TRAIN":     [ "RAIL", "HIGHSPEED_RAIL", "LONG_DISTANCE", "NIGHT_RAIL", "REGIONAL_FAST_RAIL", "REGIONAL_RAIL", "SUBURBAN" ],
    "BOAT":      [ "FERRY" ],
    "FUNICULAR": [ "FUNICULAR", "AREAL_LIFT", "CABLE_CAR" ],
};

const TRANSIT_PREFS_ICON_BASE_PATH = "/assets/icon/transit_prefs";

/** Image selector options for transit mode preferences (Bus → Tram → Métro → Train → Bateau → Funiculaire). */
export const TRANSIT_MODE_UI_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "BUS",
        "label":    "Bus",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/bus.webp`,
    },
    {
        "id":       "TRAM",
        "label":    "Tram",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/tram.webp`,
    },
    {
        "id":       "METRO",
        "label":    "Métro",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/metro.webp`,
    },
    {
        "id":       "TRAIN",
        "label":    "Train",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/train.webp`,
    },
    {
        "id":       "BOAT",
        "label":    "Bateau",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/boat.webp`,
    },
    {
        "id":       "FUNICULAR",
        "label":    "Funiculaire",
        "imageSrc": `${ TRANSIT_PREFS_ICON_BASE_PATH }/funicular.webp`,
    },
];

/**
 * Type guard for {@link TransitModeCategoryId}.
 *
 * @param value Candidate string from persistence or the selector.
 * @returns `true` when `value` is a supported transit mode category id.
 */
export const isTransitModeCategoryId = ( value: string ): value is TransitModeCategoryId => {
    return TRANSIT_MODE_CATEGORY_IDS.includes( value as TransitModeCategoryId );
};

/**
 * Maps legacy persisted category ids to the current vocabulary.
 *
 * @param categoryId Raw category id from an older persisted envelope.
 * @returns Normalized category id, or `undefined` when unmappable.
 */
export const mapLegacyTransitModeCategoryId = ( categoryId: string ): TransitModeCategoryId | undefined => {
    if ( categoryId === "FERRY" ) {
        return "BOAT";
    }

    if ( isTransitModeCategoryId( categoryId ) ) {
        return categoryId;
    }

    return undefined;
};

/**
 * Normalizes persisted enabled mode lists after migration or schema parse.
 *
 * @param enabledModes Raw enabled category ids.
 * @returns Deduplicated supported category ids preserving first-seen order.
 */
export const normalizeEnabledTransitModeCategories = ( enabledModes: readonly string[] ): TransitModeCategoryId[] => {
    const normalized: TransitModeCategoryId[] = [];

    for ( const rawCategoryId of enabledModes ) {
        const categoryId = mapLegacyTransitModeCategoryId( rawCategoryId );
        if ( categoryId === undefined || normalized.includes( categoryId ) ) {
            continue;
        }

        normalized.push( categoryId );
    }

    return normalized;
};

/**
 * Expands enabled UI categories into MOTIS `transitModeFilters` for itinerary search.
 *
 * @param enabledModes Enabled transit mode categories from persistence.
 * @returns Deduplicated MOTIS mode tokens; always includes {@link ALWAYS_INCLUDED_TRANSIT_MODE_FILTER}.
 */
export const toTransitModeFilters = ( enabledModes: readonly TransitModeCategoryId[] ): ApiTransitModeFilter[] => {
    const motisIds = new Set<ApiTransitModeFilter>( [ ALWAYS_INCLUDED_TRANSIT_MODE_FILTER ] );

    for ( const categoryId of enabledModes ) {
        for ( const motisId of TRANSIT_MODE_CATEGORY_TO_MOTIS_IDS[categoryId] ) {
            motisIds.add( motisId as ApiTransitModeFilter );
        }
    }

    return [ ...motisIds ];
};
